import axios from 'axios'
import { IMPERSONATE_WEBHOOK_HEADER } from '../libs/impersonation'

/**
 * Minimal de ce dont on a besoin pour minter un JWT Appwrite (account.createJWT()).
 * Typé localement pour rester testable sans dépendre du SDK complet.
 */
export interface JwtMinter {
   createJWT(): Promise<{ jwt: string }>
}

/** En-têtes d'un appel webhook : Authorization, + la cible d'impersonation si elle est fournie. */
export function buildWebhookHeaders(jwt: string, impersonateTarget?: string | null): Record<string, string> {
   const headers: Record<string, string> = { Authorization: `Bearer ${jwt}` }
   const target = (impersonateTarget ?? '').trim()
   if (target) headers[IMPERSONATE_WEBHOOK_HEADER] = target
   return headers
}

/**
 * POST authentifié vers le webhook avec un JWT Appwrite court, au lieu de
 * l'ancienne clé partagée publique (cf. docs/SECURITE_API_WEBHOOK.md).
 *
 * Le JWT est minté juste avant l'appel (TTL ~15 min). En cas de 401 (typiquement
 * une expiration en cours de batch de sync), on re-minte une fois et on réessaie.
 *
 * Fonction pure (dépendances injectées) → testable unitairement.
 */
export async function postWebhookWithJwt(
   account: JwtMinter,
   endpoint: string,
   path: string,
   body: object = {},
   impersonateTarget: string | null = null,
) {
   const doPost = async () => {
      const { jwt } = await account.createJWT()
      return axios.post(`${endpoint}${path}`, body, {
         headers: buildWebhookHeaders(jwt, impersonateTarget),
      })
   }
   try {
      return await doPost()
   } catch (err: any) {
      if (err?.response?.status === 401) {
         console.warn('postWebhookWithJwt: 401 → re-mint JWT et retry', path)
         return await doPost()
      }
      throw err
   }
}
